import { PureComponent, createContext } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import api from "../services";
import {
  STORAGE_KEYS,
  DIRECTION,
  HEADER,
  SEARCH_PARAMS,
  FILE_UPLOAD_STATUS,
  BACK_TO_LOGIN_PATHS,
  API_ENDPOINTS,
  AUTH_FLOW,
  EVENT_NAME,
} from "../constants";
import i18n from "../translations";
import { getSessionStorage, setSessionStorage } from "../utils/storageUtils";
import { changeColorScheme, changeFontFamily } from "../utils/themeUtils";
import { CustomSplash } from "../components";
import { getParamsFromUrl } from "../utils";
import { logAnalyticEvent, setDefaultParams } from "../analytics";
import { jwtDecode } from "jwt-decode";

export const AppContext = createContext({
  changeLanguage: () => {},
  handleOnboardingCompleted: () => {},
  handleBackToOnboarding: () => {},
  getUserDetails: () => {},
  handleIsTCAccepted: () => {},
  setPhoneNumber: () => {},
  setCRNumber: () => {},
  handleFinanceJourneyStatus: () => {},
  handleEligibilityData: () => {},
  updateUserDetails: () => {},
  getFinanceApplications: () => {},
  handleDynamicRegex: () => {},
  setIsWathqVerify: () => {},
  handleSetProvider: () => {},
  handleFileSelect: () => {},
  handleSetFileUploadStatus: () => {},
  handleSetFileUploadProgress: () => {},
  handleSetFileUploadDetails: () => {},
  handleResetFile: () => {},
  handlePreLoginCompleted: () => {},
  getTenantConfig: () => {},
  handleAuthConnect: () => {},
  handleGetProviders: () => {},
  handleBackPreLogin: () => {},
  handleCheckBoxToggle: () => {},
  handleSelectedBanks: () => {},
  handleConsentCheck: () => {},
  handleResetGroupFiles: () => {},
  tenantConfig: null,
  versionCheckData: null,
  selectedLanguage: null,
  showOnboarding: true,
  isRTL: false,
  userDetails: null,
  isTCAccepted: false,
  phoneNumber: "",
  financeJourneyStatus: null,
  financeDetails: {},
  isRegexValid: [],
  CRNumber: "",
  isWathqVerify: false,
  provider: null,
  uploadFiles: {},
  channel: null,
  tenant: null,
  flow: null,
  product: null,
  customPath: null,
  nafathService: null,
  preLoginScreens: [],
  fileUploadStatus: {},
  fileUploadProgress: {},
  fileUploadDetails: {},
  preLoginCompleted: false,
  providers: [],
  selectedBanks: [],
  consent: {},
});

class AppProvider extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      tenantConfig: null,
      versionCheckData: null,
      selectedLanguage: null,
      isPersistedDataLoaded: false,
      showOnboarding: true,
      isRTL: false,
      userDetails: null,
      isTCAccepted: false,
      phoneNumber: "",
      financeJourneyStatus: null,
      eligibilityPolicyData: null,
      financeDetails: {},
      isRegexValid: [],
      CRNumber: "",
      isWathqVerify: false,
      loginUrl: "",
      provider: null,
      uploadFiles: {},
      channel: null,
      tenant: null,
      flow: null,
      product: null,
      customPath: null,
      nafathService: null,
      preLoginScreens: [],
      fileUploadStatus: {},
      fileUploadProgress: {},
      fileUploadDetails: {},
      preLoginCompleted: false,
      connectAuth: null,
      providers: [],
      fleetFinanceAssets: [],
      selectedFinanceAssets: [],
      selectedBanks: [],
    };
  }

  async componentDidMount() {
    try {
      const searchParams = getParamsFromUrl(
        decodeURIComponent((window.location + "").replace(/\+/g, "%20"))
      );
      const pathParts = window.location.pathname.split("/");
      const preferredLanguage =
        searchParams?.lang || getSessionStorage(STORAGE_KEYS.PREFERED_LANGUAGE);

      this.checkIsOnboardingCompleted();
      if (
        pathParts[pathParts.length - 1] === BACK_TO_LOGIN_PATHS.BACK_PRE_LOGIN
      ) {
        this.handleBackPreLogin();
      } else {
        this.checkIsPreLoginCompleted();
      }
      // await this.handleVersionCheck();
      await this.getTenantConfig();
      searchParams?.code && (await this.handleValidateLogin());
      logAnalyticEvent(EVENT_NAME.ORIGINAL_LANGUAGE, {
        original_language: preferredLanguage === "ar" ? "العربية" : "English",
      });
    } catch (error) {
      console.error("Error in context componentDidMount method: ", error);
    } finally {
      this.setState({ isPersistedDataLoaded: true });
    }
  }

  setAppMeta = (title, faviconUrl) => {
    document.title = title;

    let favicon = document.querySelector("link[rel~='icon']");
    if (favicon) {
      favicon.href = faviconUrl;
    } else {
      favicon = document.createElement("link");
      favicon.rel = "icon";
      favicon.href = faviconUrl;
      document.head.appendChild(favicon);
    }
  };

  handleSetProvider = (provider) => {
    setSessionStorage(STORAGE_KEYS.PROVIDER_DATA, {
      displayName: provider?.displayName,
      arabicDisplayName: provider?.arabicDisplayName,
      providerId: provider?.providerId,
    });
    this.setState({ provider: provider });
  };

  handleFileSelect = (fileKey, uploadFile) => {
    this.setState((prevState) => ({
      uploadFiles: {
        ...prevState.uploadFiles,
        [fileKey]: uploadFile,
      },
    }));
  };

  handleSetFileUploadStatus = (fileKey, fileUploadStatus) => {
    this.setState((prevState) => ({
      fileUploadStatus: {
        ...prevState.fileUploadStatus,
        [fileKey]: fileUploadStatus,
      },
    }));
  };

  handleSetFileUploadProgress = (fileKey, fileUploadProgress) => {
    this.setState((prevState) => ({
      fileUploadProgress: {
        ...prevState.fileUploadProgress,
        [fileKey]: fileUploadProgress,
      },
    }));
  };

  handleSetFileUploadDetails = (fileKey, fileUploadDetails) => {
    this.setState((prevState) => ({
      fileUploadDetails: {
        ...prevState.fileUploadDetails,
        [fileKey]: fileUploadDetails,
      },
    }));
  };

  handleResetFile = (fileKey) => {
    this.setState((prevState) => ({
      fileUploadStatus: {
        ...prevState.fileUploadStatus,
        [fileKey]: FILE_UPLOAD_STATUS.NOT_STARTED,
      },
      fileUploadProgress: {
        ...prevState.fileUploadProgress,
        [fileKey]: 0,
      },
      fileUploadDetails: {
        ...prevState.fileUploadDetails,
        [fileKey]: {},
      },
      uploadFiles: {
        ...prevState.uploadFiles,
        [fileKey]: null,
      },
    }));
  };

  handleResetGroupFiles = (groupKey) => {
    this.setState((prevState) => {
      const resetByGroup = (obj = {}) =>
        Object.keys(obj).reduce((acc, key) => {
          if (!key.startsWith(groupKey)) {
            acc[key] = obj[key];
          }
          return acc;
        }, {});

      return {
        fileUploadStatus: resetByGroup(prevState.fileUploadStatus),
        fileUploadProgress: resetByGroup(prevState.fileUploadProgress),
        fileUploadDetails: resetByGroup(prevState.fileUploadDetails),
        uploadFiles: resetByGroup(prevState.uploadFiles),
      };
    });
  };
  checkIsOnboardingCompleted = () => {
    const hasOnboarding = getSessionStorage(STORAGE_KEYS.ONBOARDING_COMPLETED);
    this.setState({ showOnboarding: !hasOnboarding });
  };

  handleOnboardingCompleted = (timeSpent) => {
    setSessionStorage(STORAGE_KEYS.ONBOARDING_COMPLETED, true);
    this.setState({ showOnboarding: false });
  };

  handleBackToOnboarding = () => {
    setSessionStorage(STORAGE_KEYS.ONBOARDING_COMPLETED, false);
    this.setState({ showOnboarding: true });
  };

  checkIsPreLoginCompleted = () => {
    const hasPreLogin = getSessionStorage(STORAGE_KEYS.PRE_LOGIN_COMPLETED);
    this.setState({ preLoginCompleted: hasPreLogin });
  };

  handlePreLoginCompleted = () => {
    setSessionStorage(STORAGE_KEYS.PRE_LOGIN_COMPLETED, true);
    this.setState({ preLoginCompleted: true });
  };

  handleBackPreLogin = () => {
    setSessionStorage(STORAGE_KEYS.PRE_LOGIN_COMPLETED, false);
    this.setState({ preLoginCompleted: false });
  };

  changeLanguage = (languageCode) => {
    const { tenantConfig } = this.state;
    this.setAppMeta(
      tenantConfig?.favicon?.content?.title
        ? tenantConfig?.favicon?.content?.title[languageCode]
        : tenantConfig?.favicon?.title[languageCode],
      tenantConfig?.favicon?.content?.logo
        ? tenantConfig?.favicon?.content?.logo
        : tenantConfig?.favicon?.content?.dark
    );

    changeFontFamily(tenantConfig?.mobile_font_styling?.content, languageCode);
    const accessToken = getSessionStorage(HEADER.AUTHORIZATION);
    const selectedLanguage = tenantConfig?.supported_languages.content.filter(
      (item) => item.value === languageCode
    )[0];
    i18n.changeLanguage(languageCode).then(async () => {
      setSessionStorage(STORAGE_KEYS.PREFERED_LANGUAGE, languageCode);
      accessToken && (await this.updateUserDetails(languageCode));
    });
    this.setState({
      selectedLanguage: selectedLanguage ?? null,
      isRTL: selectedLanguage?.direction === DIRECTION.RTL,
    });
  };

  handleAuthConnect = async () => {
    try {
      const response = await api.getConnectAuth({
        customer_id: `${this.state.userDetails.id}`,
      });
      setSessionStorage(STORAGE_KEYS.AUTH_CONNECT, response?.data);
    } catch (error) {
      console.error("Error in handleAuthConnect api:", error);
    }
  };

  handleGetProviders = async () => {
    const connectAuth = getSessionStorage(STORAGE_KEYS.AUTH_CONNECT);
    try {
      const response = await axios.get(
        API_ENDPOINTS.GET_PROVIDERS(connectAuth?.base_url),
        {
          headers: {
            Authorization: `Bearer ${connectAuth?.accessToken}`,
          },
        }
      );
      this.setState({ providers: response?.data?.providers });
    } catch (error) {
      console.error("Bank API Error:", error);
    }
  };

  handleValidateLogin = async () => {
    const searchParams = getParamsFromUrl(
      decodeURIComponent((window.location + "").replace(/\+/g, "%20"))
    );
    try {
      const payload = {
        code: searchParams?.code,
        redirect_uri: `${window.location.origin}/company-details`,
        client_id: searchParams?.flow,
      };
      const response = await api.validateLogin(payload);
      setSessionStorage(HEADER.USER_ID, response.data.id);
    } catch (error) {
      console.error("Error in handleValidateLogin api:", error);
    }
  };

  getTenantConfig = async () => {
    const pathParts = window.location.pathname.split("/");
    const slug = pathParts[pathParts.length - 1];
    const storePath = getSessionStorage(STORAGE_KEYS.CUSTOM_PATH);
    const searchParams = getParamsFromUrl(
      decodeURIComponent((window.location + "").replace(/\+/g, "%20"))
    );
    try {
      const response = await api.appConfig(storePath ? storePath : slug);
      const tenantConfig = response.data?.config;
      tenantConfig?.supported_languages?.content.forEach((language) => {
        changeFontFamily(
          tenantConfig?.mobile_font_styling?.content,
          language?.value
        );
      });

      changeColorScheme(tenantConfig?.mobile_theme?.content?.colorsLight);

      const preferredLanguage =
        searchParams?.lang || getSessionStorage(STORAGE_KEYS.PREFERED_LANGUAGE);

      this.setAppMeta(
        tenantConfig?.favicon?.content?.title
          ? tenantConfig?.favicon?.content?.title[preferredLanguage]
          : tenantConfig?.favicon?.title[preferredLanguage],
        tenantConfig?.favicon?.content?.logo
          ? tenantConfig?.favicon?.content?.logo
          : tenantConfig?.favicon?.content?.dark
      );
      setSessionStorage(
        STORAGE_KEYS.DEFAULT_LANGUAGE,
        tenantConfig.default_language_code
      );
      setSessionStorage(
        STORAGE_KEYS.PREFERED_LANGUAGE,
        preferredLanguage ?? tenantConfig.default_language_code
      );

      setSessionStorage(HEADER.CHANNEL_ID, response.data?.channel?.slug);
      setSessionStorage(HEADER.PRODUCT_SLUG, response.data?.product_slug);
      setSessionStorage(SEARCH_PARAMS.FLOW, response.data?.auth_flow);
      setSessionStorage(SEARCH_PARAMS.TENANT, response.data?.tenant?.slug);
      setSessionStorage(STORAGE_KEYS.CUSTOM_PATH, response.data?.custom_path);
      setSessionStorage(STORAGE_KEYS.PRODUCT_ID, response.data?.product_id);

      setSessionStorage(
        STORAGE_KEYS.CAN_RUN_CAMPAIGN,
        response.data?.can_run_campaigns
      );
      setSessionStorage(
        STORAGE_KEYS.LOADER,
        tenantConfig?.mobile_loader?.content?.loader
      );
      setSessionStorage(HEADER.REQUEST_TENANT, tenantConfig.id);

      const selectedLanguage = tenantConfig.supported_languages.content.filter(
        (item) =>
          preferredLanguage
            ? item?.value === preferredLanguage
            : item?.value === tenantConfig?.default_language_code
      )[0];
      this.setState(
        {
          tenantConfig,
          selectedLanguage,
          channel: response.data?.channel?.slug,
          tenant: response.data?.tenant?.slug,
          flow: response.data?.auth_flow,
          product: response.data?.product_slug,
          customPath: response.data?.custom_path,
          nafathService: response.data?.nafath_service_name,
          preLoginScreens: response.data?.prelogin_screens,
        },
        () => {
          this.changeLanguage(selectedLanguage?.value);
          response.data?.auth_flow !== AUTH_FLOW.CUSTOM_JOURNEY &&
            this.getLoginUrl();
        }
      );
    } catch (error) {
      console.error("Error in getTenantConfig api:", error);
    }
  };

  getUserDetails = async () => {
    try {
      const response = await api.getUserDetails();
      const accessToken = getSessionStorage(HEADER.AUTHORIZATION);
      const { sid } = jwtDecode(accessToken);
      setDefaultParams({
        user_session_id: sid,
        app_user_id: response?.data?.id,
      });
      setSessionStorage(HEADER.USER_ID, response.data.id);
      this.setState({ userDetails: response.data });
    } catch (error) {
      console.error("Error fetching user details", error);
    }
  };

  updateUserDetails = async (languageCode) => {
    try {
      const payload = { preferred_language: languageCode };
      const response = await api.updateUserDetails(payload);
      this.setState({ userDetails: response.data });
    } catch (error) {
      console.error("Error update user details", error);
    }
  };

  handleIsTCAccepted = (isTCAccepted) => {
    this.setState({ isTCAccepted });
  };

  handleConsentCheck = (newConsentState = {}, options = {}) => {
    const { reset = false } = options;
    if (reset) {
      this.setState((prevState) => ({
        ...prevState,
        consent: {},
      }));
      return;
    }

    if (!newConsentState || typeof newConsentState !== "object") return;

    this.setState((prevState) => ({
      ...prevState,
      consent: {
        ...prevState.consent,
        ...newConsentState,
      },
    }));
  };

  setPhoneNumber = (phoneNumber) => {
    this.setState({ phoneNumber });
  };

  setCRNumber = (CRNumber) => {
    this.setState({ CRNumber });
  };

  setIsWathqVerify = () => {
    this.setState({ isWathqVerify: true });
  };

  handleFinanceJourneyStatus = async () => {
    const searchParams = getParamsFromUrl(
      decodeURIComponent((window.location + "").replace(/\+/g, "%20"))
    );
    const tenantName = getSessionStorage(SEARCH_PARAMS.TENANT);
    const product = getSessionStorage(HEADER.PRODUCT_SLUG);
    const channelId = getSessionStorage(HEADER.CHANNEL_ID);
    const partnerTenantId = getSessionStorage(STORAGE_KEYS.PARTNER_TENANT_ID);
    const localFormState = getSessionStorage(STORAGE_KEYS.LOCAL_FORM_STATE);

    try {
      const financeJourney = await api.currentApplicationFlow({
        product_name: product,
        partner_tenant_id: partnerTenantId,
        variables: {
          redirect_url: `${window.location.origin}/${
            window.location.pathname.split("/")[1]
          }?${SEARCH_PARAMS.TENANT}=${tenantName}&${
            SEARCH_PARAMS.PRODUCT
          }=${product}&${SEARCH_PARAMS.CHANNEL_ID}=${channelId}`,
          in_annual_revenue: localFormState?.annual_revenue,
          in_required_amount: localFormState?.amount_required,
        },
      });
      this.setState({
        financeJourneyStatus: financeJourney.data,
      });
    } catch (error) {
      console.error("Error fetching finance journey status: ", error);
    }
  };

  handleEligibilityData = (eligibilityPolicy) => {
    const eligible =
      eligibilityPolicy &&
      eligibilityPolicy.value &&
      eligibilityPolicy.value.filter((policy) => policy.is_eligible);
    const nonEligible =
      eligibilityPolicy &&
      eligibilityPolicy.value &&
      eligibilityPolicy.value.filter((policy) => !policy.is_eligible);

    this.setState({
      eligibilityPolicyData: [
        {
          id: "eligible",
          label: "Eligible",
          value: !!eligible ? eligible.length : 0,
          color: "#2EC465",
          data: eligible,
        },
        {
          id: "non-eligible",
          label: "Non-eligible",
          value: !!nonEligible ? nonEligible.length : 0,
          color: "#FFAD0D",
          data: nonEligible,
        },
      ],
    });
  };

  getFinanceApplications = async (financeApplicationId) => {
    try {
      const response = await api.getFinanceApplications(financeApplicationId);
      this.setState({ financeDetails: response.data });
    } catch (error) {
      console.error("Error in getFinanceApplications api:", error);
    }
  };

  getLoginUrl = async () => {
    const flow = getSessionStorage(SEARCH_PARAMS.FLOW);
    try {
      const response = await api.login({
        redirect_uri: `${window.location.origin}/company-details`,
        client_id: flow,
      });
      this.setState({ loginUrl: response.data?.url });
    } catch (error) {
      console.error("Error in getAppAssets api:", error);
    }
  };

  handlePartnerLogin = async () => {
    const { searchParams } = this.props.router;
    const partnerId = searchParams.get(SEARCH_PARAMS.PARTNER_ID);
    const partnerToken = searchParams.get(SEARCH_PARAMS.PARTNER_TOKEN);

    try {
      const payload = {
        partner_id: partnerId,
        token: partnerToken,
      };
      const response = await api.partnerLogin(payload);
    } catch (error) {
      console.error("Error in handlePartnerLogin api:", error);
    }
  };

  handleCheckBoxToggle = ({
    selectedState,
    selectedItem, // Now it's the complete item object
    totalItems,
    itemState,
  }) => {
    this.setState((prev) => {
      const currentArray = prev[selectedState] || [];
      const sequenceNo = selectedItem.sequenceNo || selectedItem.id;
      const exists = currentArray.some(
        (item) => (item.sequenceNo || item.id) === sequenceNo
      );

      let newArray;

      if (exists) {
        newArray = currentArray.filter(
          (item) => (item.sequenceNo || item.id) !== sequenceNo
        );
      } else {
        newArray = [...currentArray, selectedItem];
      }

      return {
        [selectedState]: newArray,
        [itemState]: totalItems,
      };
    });
  };

  handleDynamicRegex = (isValid) => {
    this.setState({ isRegexValid: isValid });
  };

  handleSelectedBanks = (selectedBanks) => {
    this.setState({ selectedBanks });
  };

  render() {
    const { children } = this.props;
    const { isPersistedDataLoaded, isRTL } = this.state;

    return (
      <AppContext.Provider
        value={{
          ...this.state,
          changeLanguage: this.changeLanguage,
          handleOnboardingCompleted: this.handleOnboardingCompleted,
          handleBackToOnboarding: this.handleBackToOnboarding,
          getUserDetails: this.getUserDetails,
          handleIsTCAccepted: this.handleIsTCAccepted,
          setPhoneNumber: this.setPhoneNumber,
          setCRNumber: this.setCRNumber,
          handleFinanceJourneyStatus: this.handleFinanceJourneyStatus,
          handleEligibilityData: this.handleEligibilityData,
          updateUserDetails: this.updateUserDetails,
          getFinanceApplications: this.getFinanceApplications,
          handleDynamicRegex: this.handleDynamicRegex,
          setIsWathqVerify: this.setIsWathqVerify,
          handleSetProvider: this.handleSetProvider,
          handleFileSelect: this.handleFileSelect,
          handleSetFileUploadStatus: this.handleSetFileUploadStatus,
          handleSetFileUploadProgress: this.handleSetFileUploadProgress,
          handleSetFileUploadDetails: this.handleSetFileUploadDetails,
          handleResetFile: this.handleResetFile,
          handlePreLoginCompleted: this.handlePreLoginCompleted,
          getTenantConfig: this.getTenantConfig,
          handleAuthConnect: this.handleAuthConnect,
          handleGetProviders: this.handleGetProviders,
          handleBackPreLogin: this.handleBackPreLogin,
          handleCheckBoxToggle: this.handleCheckBoxToggle,
          handleSelectedBanks: this.handleSelectedBanks,
          handleConsentCheck: this.handleConsentCheck,
          handleResetGroupFiles: this.handleResetGroupFiles,
        }}
      >
        {isPersistedDataLoaded ? (
          <div dir={isRTL ? "rtl" : "ltr"}>{children}</div>
        ) : (
          <CustomSplash />
        )}
      </AppContext.Provider>
    );
  }
}

export const withRouter = (Component) => {
  const Wrapper = (props) => {
    const [searchParams] = useSearchParams();

    return <Component {...props} router={{ searchParams }} />;
  };

  return Wrapper;
};

export const withApp = (Wrapped) => (props) => {
  return (
    <AppContext.Consumer>
      {(value) => <Wrapped {...props} contextValue={value} />}
    </AppContext.Consumer>
  );
};

export default withRouter(AppProvider);
